import { clamp } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

interface CardLayoutOptions {
  itemsCount: number;
  maxRows?: number;
  availableHeight?: number;
  minCardWidth?: number;
  maxCardWidth?: number;
  minCardHeight?: number;
  maxCardHeight?: number;
  gap?: number;
  trailingNextWidth?: number;
}

interface CardLayoutResult {
  itemWidth: number;
  itemHeight: number;
  rowsCount: number;
  handleResize: (el: HTMLElement) => void;
  trailingNextWidth: number;
  maxHeight: number;
  minHeight: number;
  isCoverFullHeight: boolean;
}

export const useCardLayout = ({
  itemsCount,
  maxRows = 0,
  availableHeight = 0,
  minCardWidth = 140,
  maxCardWidth = 300,
  minCardHeight = 160,
  maxCardHeight = 200,
  gap = 8,
  trailingNextWidth = 20,
}: CardLayoutOptions): CardLayoutResult => {
  const containerRef = useRef<HTMLElement | null>(null);
  const [itemWidth, setItemWidth] = useState(maxCardWidth);
  const [itemHeight, setItemHeight] = useState(minCardHeight);
  const [rowsCount, setRowsCount] = useState(1);

  // keep math stable on first render
  const safeMaxHeight =
    Number.isFinite(availableHeight) && availableHeight > 0
      ? availableHeight
      : 0;

  const availableRowsCount =
    safeMaxHeight > 0
      ? Math.max(1, Math.floor((safeMaxHeight + gap) / (minCardHeight + gap)))
      : 1;

  const calculateLayout = useCallback(
    (el: HTMLElement) => {
      if (!(el?.offsetWidth && el?.offsetHeight)) {
        return;
      }

      const safeItemsCount = Math.max(itemsCount, 1);
      const availableWidth = el.offsetWidth - trailingNextWidth;

      const maxItemsPerRow = Math.floor(
        (availableWidth + gap) / (minCardWidth + gap),
      );
      const minItemsPerRow = Math.ceil(
        (availableWidth + gap) / (maxCardWidth + gap),
      );

      const maxRowsCount = Math.ceil(safeItemsCount / minItemsPerRow);

      const newRowsCount = Math.min(
        availableRowsCount,
        maxRows || availableRowsCount,
        maxRowsCount,
      );

      const itemsPerRow = clamp(
        Math.ceil(safeItemsCount / (newRowsCount || 1)),
        minItemsPerRow,
        maxItemsPerRow,
      );

      let newItemWidth =
        (availableWidth - (itemsPerRow - 1) * gap) / itemsPerRow;

      if (newItemWidth > maxCardWidth) {
        const nextItemsPerRow = itemsPerRow + 1;
        newItemWidth =
          (availableWidth - (nextItemsPerRow - 1) * gap) / nextItemsPerRow;
      }
      const newItemHeight = clamp(
        (el.offsetHeight - (rowsCount - 1) * gap) / rowsCount,
        minCardHeight,
        maxCardHeight,
      );

      const finalWidth = clamp(newItemWidth, minCardWidth, maxCardWidth);

      setRowsCount(newRowsCount);
      setItemWidth(finalWidth);
      setItemHeight(newItemHeight);
    },
    [
      itemsCount,
      trailingNextWidth,
      gap,
      minCardWidth,
      maxCardWidth,
      availableRowsCount,
      maxRows,
      rowsCount,
      minCardHeight,
      maxCardHeight,
    ],
  );

  const handleResize = useCallback(
    (el: HTMLElement) => {
      containerRef.current = el;
      calculateLayout(el);
    },
    [calculateLayout],
  );

  useEffect(() => {
    if (containerRef.current) {
      calculateLayout(containerRef.current);
    }
  }, [calculateLayout, maxRows, availableRowsCount]);

  const minHeight = rowsCount * minCardHeight + (rowsCount - 1) * gap;
  const maxHeight = rowsCount * maxCardHeight + (rowsCount - 1) * gap;

  const isCoverFullHeight = availableHeight > 0 && maxHeight >= availableHeight;

  return {
    isCoverFullHeight,
    minHeight,
    maxHeight,
    itemWidth,
    itemHeight,
    rowsCount,
    handleResize,
    trailingNextWidth,
  };
};
